import { useMutation, useQuery } from '@tanstack/react-query';

import performanceCompanyPreferencesService from './performanceCompanyPreferencesService';

import type {
    EmployeePortalCompetenciesUpdateRequest,
    MultiplePillarsPerObjectiveUpdateRequest,
    ObjectiveWithoutPillarUpdateRequest,
    PerformanceCompanyPreferencesUpdateRequest,
    StrategicVisionUpdateRequest,
} from '@/types/performanceCompanyPreferences';

import { queryClient } from '@/AppWithProviders';

export const COMPANY_PREFERENCES_QUERY_KEY = ['companyPreferences'];

export const useCompanyPreferences = () => {
    return useQuery({
        queryKey: COMPANY_PREFERENCES_QUERY_KEY,
        queryFn: () => performanceCompanyPreferencesService.getCompanyPreferences(),
    });
};

export const useUpdateCompanyPreferences = () => {
    return useMutation({
        mutationFn: (request: PerformanceCompanyPreferencesUpdateRequest) =>
            performanceCompanyPreferencesService.updateCompanyPreferences(request),
    });
};

export const useUpdateStrategicVision = () => {
    return useMutation({
        mutationFn: (request: StrategicVisionUpdateRequest) =>
            performanceCompanyPreferencesService.updateStrategicVision(request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: COMPANY_PREFERENCES_QUERY_KEY });
        },
    });
};

export const useUpdateMultiplePillarsPerObjective = () => {
    return useMutation({
        mutationFn: (request: MultiplePillarsPerObjectiveUpdateRequest) =>
            performanceCompanyPreferencesService.updateMultiplePillarsPerObjective(request),
        // The response is the saved preferences: shown at once, never the value before the save.
        onSuccess: (preferences) => {
            queryClient.setQueryData(COMPANY_PREFERENCES_QUERY_KEY, preferences);
        },
    });
};

export const useUpdateObjectiveWithoutPillar = () => {
    return useMutation({
        mutationFn: (request: ObjectiveWithoutPillarUpdateRequest) =>
            performanceCompanyPreferencesService.updateObjectiveWithoutPillar(request),
        // The response is the saved preferences: shown at once, never the value before the save.
        onSuccess: (preferences) => {
            queryClient.setQueryData(COMPANY_PREFERENCES_QUERY_KEY, preferences);
        },
    });
};

export const useUpdateEmployeePortalCompetencies = () => {
    return useMutation({
        mutationFn: (request: EmployeePortalCompetenciesUpdateRequest) =>
            performanceCompanyPreferencesService.updateEmployeePortalCompetencies(request),
        // The response is the saved preferences: shown at once, never the value before the save.
        onSuccess: (preferences) => {
            queryClient.setQueryData(COMPANY_PREFERENCES_QUERY_KEY, preferences);
        },
    });
};
